import { useCallback, useEffect, useRef, useState } from 'react';

import type { RepositoryAssetLink } from '@/api/asset-links';
import { getAssetLinks, saveAssetLinks } from '@/api/asset-links';
import { ApiError } from '@/modules/api/api-client';

// --- Types ---

interface EditableAssetLink {
  assetId: string;
  artifactIdPattern: string;
  error?: string;
}

type NotificationType = 'success' | 'error';

interface Notification {
  type: NotificationType;
  message: string;
}

// --- Sub-components ---

function LoadingSkeleton() {
  return (
    <div className="animate-pulse space-y-2">
      <div className="h-10 rounded-lg bg-gray-200 dark:bg-gray-700" />
      <div className="h-10 rounded-lg bg-gray-200 dark:bg-gray-700" />
    </div>
  );
}

function AssetLinkRow({
  link,
  index,
  onChange,
  onRemove,
  disabled,
}: {
  link: EditableAssetLink;
  index: number;
  onChange: (index: number, field: 'assetId' | 'artifactIdPattern', value: string) => void;
  onRemove: (index: number) => void;
  disabled: boolean;
}) {
  const assetIdErrorId = `asset-link-${index}-assetId-error`;

  return (
    <li className="rounded-lg border border-border p-3 space-y-2">
      <div className="flex items-start gap-3">
        <div className="flex-1 space-y-2">
          <div>
            <label
              htmlFor={`asset-link-${index}-assetId`}
              className="block text-xs font-medium text-content-secondary mb-1"
            >
              Asset Identifier *
            </label>
            <input
              id={`asset-link-${index}-assetId`}
              type="text"
              value={link.assetId}
              onChange={(e) => onChange(index, 'assetId', e.target.value)}
              placeholder="CRVS-svc-ci"
              disabled={disabled}
              maxLength={128}
              aria-invalid={!!link.error}
              aria-describedby={link.error ? assetIdErrorId : undefined}
              className={`w-full rounded-md border px-3 py-1.5 text-sm text-content bg-surface focus:outline-none focus:ring-2 focus:ring-brand-600 disabled:opacity-50 disabled:cursor-not-allowed ${
                link.error
                  ? 'border-red-500 dark:border-red-400'
                  : 'border-border'
              }`}
            />
            {link.error && (
              <p id={assetIdErrorId} role="alert" className="mt-1 text-xs text-red-700 dark:text-red-400">
                {link.error}
              </p>
            )}
          </div>
          <div>
            <label
              htmlFor={`asset-link-${index}-artifactIdPattern`}
              className="block text-xs font-medium text-content-secondary mb-1"
            >
              Artifact ID Pattern <span className="text-content-tertiary">(optional)</span>
            </label>
            <input
              id={`asset-link-${index}-artifactIdPattern`}
              type="text"
              value={link.artifactIdPattern}
              onChange={(e) => onChange(index, 'artifactIdPattern', e.target.value)}
              placeholder="CRVS-svc-ci-*"
              disabled={disabled}
              maxLength={256}
              className="w-full rounded-md border border-border px-3 py-1.5 text-sm text-content bg-surface focus:outline-none focus:ring-2 focus:ring-brand-600 disabled:opacity-50 disabled:cursor-not-allowed"
            />
          </div>
        </div>
        <button
          type="button"
          onClick={() => onRemove(index)}
          disabled={disabled}
          aria-label={`Remove asset link ${link.assetId || index + 1}`}
          className="mt-5 rounded px-2 py-1 text-xs font-medium text-red-700 hover:bg-red-50 dark:text-red-400 dark:hover:bg-red-950 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-1 disabled:opacity-50 disabled:cursor-not-allowed"
        >
          Remove
        </button>
      </div>
    </li>
  );
}

// --- Main Component ---

interface RepositoryAssetLinksProps {
  repoName: string;
}

export function RepositoryAssetLinks({ repoName }: RepositoryAssetLinksProps) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [notification, setNotification] = useState<Notification | null>(null);
  const [links, setLinks] = useState<EditableAssetLink[]>([]);

  const notificationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- Notification auto-dismiss ---
  const showNotification = useCallback((type: NotificationType, message: string) => {
    if (notificationTimerRef.current) {
      clearTimeout(notificationTimerRef.current);
    }
    setNotification({ type, message });

    if (type === 'success') {
      notificationTimerRef.current = setTimeout(() => {
        setNotification(null);
        notificationTimerRef.current = null;
      }, 5000);
    }
  }, []);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (notificationTimerRef.current) {
        clearTimeout(notificationTimerRef.current);
      }
    };
  }, []);

  // --- Load asset links ---
  const loadLinks = useCallback(async () => {
    setLoading(true);
    setNotification(null);
    try {
      const data = await getAssetLinks(repoName);
      setLinks(
        data.map((link) => ({
          assetId: link.assetId,
          artifactIdPattern: link.artifactIdPattern ?? '',
        }))
      );
    } catch {
      showNotification('error', 'Failed to load asset links. Please try again.');
      setLinks([]);
    } finally {
      setLoading(false);
    }
  }, [repoName, showNotification]);

  useEffect(() => {
    if (repoName) {
      loadLinks();
    }
  }, [repoName, loadLinks]);

  // --- Add link ---
  const handleAddLink = () => {
    setLinks((prev) => [...prev, { assetId: '', artifactIdPattern: '' }]);
  };

  // --- Remove link ---
  const handleRemoveLink = (index: number) => {
    setLinks((prev) => prev.filter((_, i) => i !== index));
  };

  // --- Update link field ---
  const handleChange = (index: number, field: 'assetId' | 'artifactIdPattern', value: string) => {
    setLinks((prev) =>
      prev.map((link, i) => {
        if (i !== index) return link;
        const updated = { ...link, [field]: value };
        // Clear validation error when assetId is modified
        if (field === 'assetId' && link.error) {
          updated.error = undefined;
        }
        return updated;
      })
    );
  };

  // --- Client-side validation ---
  const validate = (): boolean => {
    let valid = true;
    const updatedLinks = links.map((link) => {
      if (!link.assetId.trim()) {
        valid = false;
        return { ...link, error: 'Asset identifier is required.' };
      }
      return { ...link, error: undefined };
    });
    setLinks(updatedLinks);
    return valid;
  };

  // --- Save ---
  const handleSave = async () => {
    if (!validate()) return;

    setSaving(true);
    setNotification(null);

    const payload: RepositoryAssetLink[] = links.map((link) => ({
      assetId: link.assetId.trim(),
      ...(link.artifactIdPattern.trim()
        ? { artifactIdPattern: link.artifactIdPattern.trim() }
        : {}),
    }));

    try {
      const saved = await saveAssetLinks(repoName, payload);
      setLinks(
        saved.map((link) => ({
          assetId: link.assetId,
          artifactIdPattern: link.artifactIdPattern ?? '',
        }))
      );
      showNotification('success', 'Asset links saved successfully.');
    } catch (err) {
      if (err instanceof ApiError && err.status === 400) {
        const body = err.body as { message?: string } | undefined;
        showNotification('error', body?.message ?? 'Validation failed. Please check the form fields.');
      } else {
        showNotification('error', 'Failed to save asset links. Please try again.');
      }
    } finally {
      setSaving(false);
    }
  };

  // --- Render: Loading state ---
  if (loading) {
    return (
      <section className="mt-6">
        <h3 className="text-sm font-semibold text-content mb-3">Deployable Asset Links</h3>
        <LoadingSkeleton />
      </section>
    );
  }

  // --- Render: Main UI ---
  return (
    <section className="mt-6 space-y-4">
      {/* Header with Add Link button */}
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-semibold text-content">Deployable Asset Links</h3>
        <button
          type="button"
          onClick={handleAddLink}
          disabled={saving}
          className="inline-flex items-center gap-1.5 rounded-md bg-accent-700 px-3 py-1.5 text-xs font-medium text-white hover:bg-accent-800 focus:outline-none focus:ring-2 focus:ring-accent-600 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" aria-hidden="true">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Add Link
        </button>
      </div>

      {/* Notification */}
      {notification && (
        <div
          role={notification.type === 'error' ? 'alert' : 'status'}
          className={`rounded-lg border p-3 ${
            notification.type === 'success'
              ? 'border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-900/20'
              : 'border-red-200 dark:border-red-800 bg-red-50 dark:bg-red-900/20'
          }`}
        >
          <p
            className={`text-sm ${
              notification.type === 'success'
                ? 'text-green-700 dark:text-green-300'
                : 'text-red-700 dark:text-red-300'
            }`}
          >
            {notification.message}
          </p>
        </div>
      )}

      {/* Asset links list */}
      {links.length === 0 && (
        <div className="rounded-lg border border-dashed border-border p-4 text-center">
          <p className="text-sm text-content-tertiary">
            No asset links configured. Add one to link this repository to a deployable asset.
          </p>
        </div>
      )}

      {links.length > 0 && (
        <ul className="space-y-2">
          {links.map((link, index) => (
            <AssetLinkRow
              key={index}
              link={link}
              index={index}
              onChange={handleChange}
              onRemove={handleRemoveLink}
              disabled={saving}
            />
          ))}
        </ul>
      )}

      {/* Save button */}
      <div className="pt-2">
        <button
          type="button"
          onClick={handleSave}
          disabled={saving}
          className="inline-flex items-center gap-2 rounded-md bg-brand-700 px-4 py-2 text-sm font-medium text-white hover:bg-brand-800 focus:outline-none focus:ring-2 focus:ring-brand-600 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {saving && (
            <svg
              className="h-4 w-4 animate-spin"
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
            </svg>
          )}
          {saving ? 'Saving...' : 'Save'}
        </button>
      </div>
    </section>
  );
}
